import { useEffect, useRef } from 'react'

export type PopupTriggerMode = 'any' | 'timer' | 'scroll' | 'exit-intent'

interface PopupTriggerProps {
  onTrigger: () => void
  mode?: PopupTriggerMode
  timerMs?: number
  scrollPercent?: number
}

function getScrollProgressPercent(): number {
  const doc = document.documentElement
  const scrollTop = window.scrollY || doc.scrollTop
  const maxScrollable = Math.max(doc.scrollHeight - window.innerHeight, 1)
  return (scrollTop / maxScrollable) * 100
}

export function PopupTrigger({
  onTrigger,
  mode = 'any',
  timerMs = 8000,
  scrollPercent = 50,
}: PopupTriggerProps) {
  const hasTriggeredRef = useRef(false)

  useEffect(() => {
    function runTrigger() {
      if (hasTriggeredRef.current) return
      hasTriggeredRef.current = true
      onTrigger()
    }

    const shouldListenTimer = mode === 'any' || mode === 'timer'
    const shouldListenScroll = mode === 'any' || mode === 'scroll'
    const shouldListenExitIntent = mode === 'any' || mode === 'exit-intent'

    let timerId: ReturnType<typeof setTimeout> | undefined
    if (shouldListenTimer) {
      timerId = setTimeout(runTrigger, Math.max(timerMs, 0))
    }

    function onScroll() {
      if (getScrollProgressPercent() >= scrollPercent) {
        runTrigger()
      }
    }

    function onMouseOut(event: MouseEvent) {
      if (event.relatedTarget === null && event.clientY <= 0) {
        runTrigger()
      }
    }

    if (shouldListenScroll) {
      window.addEventListener('scroll', onScroll, { passive: true })
      onScroll()
    }

    if (shouldListenExitIntent) {
      document.addEventListener('mouseout', onMouseOut)
    }

    return () => {
      if (timerId) clearTimeout(timerId)
      if (shouldListenScroll) {
        window.removeEventListener('scroll', onScroll)
      }
      if (shouldListenExitIntent) {
        document.removeEventListener('mouseout', onMouseOut)
      }
    }
  }, [mode, onTrigger, scrollPercent, timerMs])

  return null
}
